import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Botao, Campo, MenuDeAcoes, Modal, Selo, useDialogo } from '../../componentes/ui';
import {
  criarContato,
  editarContato,
  excluirContato,
  type CardDetalhado,
  type ContatoDoCrm,
} from '../../servicos/crm';
import { ErroDaApi } from '../ErroDaApi';
import { formatarTelefone } from '../vinculos-trabalhistas';

/** Como o contato aparece: pelo nome; sem nome, pelo cargo. */
const nomeDoContato = (c: ContatoDoCrm): string => c.nome ?? c.cargo ?? 'Contato sem nome';

/**
 * Contatos da empresa sem acesso ao Deca — dentro de "Colaboradores", abaixo
 * de quem tem conta.
 *
 * São as pessoas com quem a operação fala antes de a empresa virar cliente:
 * vêm da Prospecção ao criar o card, ou são anotadas aqui. Não têm login nem
 * CPF e não recebem e-mail do sistema; quando alguém precisar de acesso, entra
 * pelo cadastro normal de colaborador, na aba Colaboradores da empresa.
 */
export function ContatosDoCard(props: { readonly card: CardDetalhado }): JSX.Element {
  const [formulario, setFormulario] = useState<{ contato?: ContatoDoCrm } | null>(null);
  const lista = props.card.contatos;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-3">
        <h4 className="text-xs font-medium text-conteudo-secundario">
          Contatos sem acesso ao Deca ({lista.length})
        </h4>
        <Botao variante="sutil" tamanho="pequeno" onClick={() => setFormulario({})}>
          Adicionar contato
        </Botao>
      </div>
      {lista.length === 0 ? (
        <p className="text-sm text-conteudo-secundario">
          Nenhum contato anotado. Contato é quem a operação procura na empresa — sem login no Deca.
        </p>
      ) : (
        <ul className="flex flex-col divide-y divide-borda/60 rounded-card border border-borda px-3">
          {lista.map((c) => (
            <LinhaDoContato key={c.id} contato={c} aoEditar={() => setFormulario({ contato: c })} />
          ))}
        </ul>
      )}
      {formulario !== null && (
        <FormularioDoContato
          cardId={props.card.id}
          {...(formulario.contato === undefined ? {} : { contato: formulario.contato })}
          aoFechar={() => setFormulario(null)}
        />
      )}
    </div>
  );
}

function LinhaDoContato(props: {
  readonly contato: ContatoDoCrm;
  readonly aoEditar: () => void;
}): JSX.Element {
  const c = props.contato;
  const queryClient = useQueryClient();
  const dialogo = useDialogo();
  const apagar = useMutation({
    mutationFn: () => excluirContato(c.id),
    onSettled: () => void queryClient.invalidateQueries({ queryKey: ['crm'] }),
  });
  const meios = [c.email, c.telefone === null ? null : formatarTelefone(c.telefone)].filter(
    (x) => x !== null,
  );

  return (
    <li className="flex items-start justify-between gap-3 py-2.5">
      <div className="flex min-w-0 flex-col gap-0.5">
        <span className="text-sm font-medium text-conteudo-primario">{nomeDoContato(c)}</span>
        {c.nome !== null && c.cargo !== null && (
          <span className="text-xs text-conteudo-secundario">{c.cargo}</span>
        )}
        {meios.length > 0 && (
          <span className="break-all text-xs text-conteudo-sutil">{meios.join(' · ')}</span>
        )}
        {apagar.isError && <ErroDaApi titulo="Não foi possível excluir" erro={apagar.error} />}
      </div>
      <span className="flex shrink-0 items-center gap-1">
        {c.origem === 'PROSPECCAO' && (
          <Selo tom="informativo" tamanho="pequeno">
            Prospecção
          </Selo>
        )}
        <MenuDeAcoes
          rotulo={nomeDoContato(c)}
          itens={[
            { rotulo: 'Editar', aoEscolher: props.aoEditar },
            {
              rotulo: 'Excluir',
              perigo: true,
              aoEscolher: async () => {
                const ok = await dialogo.confirmar({
                  titulo: `Excluir o contato ${nomeDoContato(c)}?`,
                  rotuloConfirmar: 'Excluir',
                  tom: 'perigo',
                });
                if (ok) apagar.mutate();
              },
            },
          ]}
        />
      </span>
    </li>
  );
}

/** Campos livres, como a operação anota; basta um preenchido. */
function FormularioDoContato(props: {
  readonly cardId: string;
  readonly contato?: ContatoDoCrm;
  readonly aoFechar: () => void;
}): JSX.Element {
  const c = props.contato;
  const queryClient = useQueryClient();
  const [nome, setNome] = useState(c?.nome ?? '');
  const [cargo, setCargo] = useState(c?.cargo ?? '');
  const [telefone, setTelefone] = useState(c?.telefone ?? '');
  const [email, setEmail] = useState(c?.email ?? '');

  const dados = {
    nome: nome.trim() || null,
    cargo: cargo.trim() || null,
    telefone: telefone.trim() || null,
    email: email.trim() || null,
  };
  const valido = Object.values(dados).some((v) => v !== null);

  const salvar = useMutation({
    mutationFn: () =>
      c === undefined ? criarContato(props.cardId, dados) : editarContato(c.id, dados),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['crm'] });
      props.aoFechar();
    },
  });

  return (
    <Modal
      aberto
      aoFechar={props.aoFechar}
      titulo={c === undefined ? 'Novo contato' : 'Editar contato'}
      descricao="Sem acesso ao Deca: não cria login nem envia e-mail."
      rodape={
        <>
          <Botao variante="secundario" onClick={props.aoFechar}>
            Cancelar
          </Botao>
          <Botao disabled={!valido} carregando={salvar.isPending} onClick={() => salvar.mutate()}>
            Salvar
          </Botao>
        </>
      }
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (valido) salvar.mutate();
        }}
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <Campo
            rotulo="Nome"
            autoFocus
            maxLength={200}
            value={nome}
            onChange={(e) => setNome(e.target.value)}
          />
          <Campo
            rotulo="Cargo"
            placeholder="Ex.: Gerente tributário"
            maxLength={100}
            value={cargo}
            onChange={(e) => setCargo(e.target.value)}
          />
          <Campo
            rotulo="Telefone"
            maxLength={60}
            value={telefone}
            onChange={(e) => setTelefone(e.target.value)}
          />
          <Campo
            rotulo="E-mail"
            maxLength={255}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        {salvar.isError && <ErroDaApi titulo="Não foi possível salvar" erro={salvar.error} />}
        <button type="submit" className="sr-only">
          Salvar
        </button>
      </form>
    </Modal>
  );
}
